import { MeshData, Color, emptyMesh, flatShade } from './mesh';

/**
 * Sampled terrain grid: `w` x `d` meters, `nx` x `nz` cells, heights from fn(x, z).
 * Also serves as the physics ground: height(x,z) bilinear, normal via finite differences.
 */
export class Heightfield {
  readonly heights: Float32Array;
  readonly cellW: number;
  readonly cellD: number;
  constructor(
    readonly w: number, readonly d: number, readonly nx: number, readonly nz: number,
    fn: (x: number, z: number) => number,
    readonly originX = -w / 2, readonly originZ = -d / 2,
  ) {
    this.cellW = w / nx; this.cellD = d / nz;
    this.heights = new Float32Array((nx + 1) * (nz + 1));
    for (let j = 0; j <= nz; j++) for (let i = 0; i <= nx; i++) {
      this.heights[j * (nx + 1) + i] = fn(originX + i * this.cellW, originZ + j * this.cellD);
    }
  }

  private h(i: number, j: number): number {
    i = i < 0 ? 0 : i > this.nx ? this.nx : i;
    j = j < 0 ? 0 : j > this.nz ? this.nz : j;
    return this.heights[j * (this.nx + 1) + i];
  }

  contains(x: number, z: number): boolean {
    return x >= this.originX && x <= this.originX + this.w && z >= this.originZ && z <= this.originZ + this.d;
  }

  /** Height at world (x,z) using the same triangle split as the mesh. Clamps outside. */
  height(x: number, z: number): number {
    const fx = (x - this.originX) / this.cellW, fz = (z - this.originZ) / this.cellD;
    let i = Math.floor(fx), j = Math.floor(fz);
    i = Math.max(0, Math.min(this.nx - 1, i)); j = Math.max(0, Math.min(this.nz - 1, j));
    const u = Math.max(0, Math.min(1, fx - i)), v = Math.max(0, Math.min(1, fz - j));
    const h00 = this.h(i, j), h10 = this.h(i + 1, j), h01 = this.h(i, j + 1), h11 = this.h(i + 1, j + 1);
    // mesh splits each cell along the (i,j)-(i+1,j+1) diagonal
    if (u + v <= 1) return h00 + (h10 - h00) * u + (h01 - h00) * v;
    return h11 + (h01 - h11) * (1 - u) + (h10 - h11) * (1 - v);
  }

  /** Approximate surface normal via central differences. */
  normal(x: number, z: number, out: { x: number; y: number; z: number }): typeof out {
    const e = Math.min(this.cellW, this.cellD) * 0.5;
    const dx = this.height(x + e, z) - this.height(x - e, z);
    const dz = this.height(x, z + e) - this.height(x, z - e);
    let nx = -dx, ny = 2 * e, nz = -dz;
    const l = Math.hypot(nx, ny, nz) || 1;
    out.x = nx / l; out.y = ny / l; out.z = nz / l;
    return out;
  }

  /** Slope angle in radians at (x,z). */
  slope(x: number, z: number): number {
    const n = this.normal(x, z, { x: 0, y: 1, z: 0 });
    return Math.acos(Math.max(-1, Math.min(1, n.y)));
  }

  /** Build a flat-shaded render mesh; colorFn receives (x, y, z, slopeRadians). */
  toMesh(colorFn: (x: number, y: number, z: number, slope: number) => Color): MeshData {
    const m = emptyMesh();
    for (let j = 0; j <= this.nz; j++) for (let i = 0; i <= this.nx; i++) {
      const x = this.originX + i * this.cellW, z = this.originZ + j * this.cellD, y = this.h(i, j);
      const c = colorFn(x, y, z, this.slope(x, z));
      m.positions.push(x, y, z); m.normals.push(0, 1, 0); m.colors.push(c[0], c[1], c[2]); m.emissive.push(0, 0, 0);
    }
    for (let j = 0; j < this.nz; j++) for (let i = 0; i < this.nx; i++) {
      const a = j * (this.nx + 1) + i, b = a + this.nx + 1;
      m.indices.push(a, b, a + 1, a + 1, b, b + 1);
    }
    return flatShade(m);
  }
}
